import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDebounce, useEffectOnce } from 'usehooks-ts';

import { useCtaReportSession, useCtaState } from '@/redux/features/cta/hooks';
import { setCtaValue } from '@/redux/features/cta/slice';
import { useAppDispatch } from '@/redux/hooks';
import { CtaPopupConfirm, CtaPopupScore } from './views';

import styles from './index.module.css';
import { usePostV1FeedbackMutation } from '@/redux/apis/frontend-api';
import { CTA_MAX_SCORE } from '@/lib/constants';
import { useTrackContent, useTrackInteraction } from '@/lib/hooks/tracking';

// This component renders both views inside the CTA form:
//   1. The score selection view
//   2. The confirmation view
const CtaPopup: FC = () => {
  const [visible, setVisible] = useState(false);
  const [confirm, setConfirm] = useState(false);
  // Mutations
  const [trackContent] = useTrackContent();
  const [trackInteraction] = useTrackInteraction();
  const [feedbackMutation] = usePostV1FeedbackMutation();
  // Handlers
  const handleSendScore = (score: number) => {
    // Do not await both mutations, feedback should not block the UI
    trackInteraction({
      content: 'cta',
      type: 'click',
      action: 'feedback',
      score,
      maxScore: CTA_MAX_SCORE,
    });
    feedbackMutation({
      body: { score, maxScore: CTA_MAX_SCORE },
    });
    setConfirm(true);
  };

  // Once rendered, show the popup by performing an animation
  const debouncedVisible = useDebounce(visible, 100);
  useLayoutEffect(() => setVisible(true), []);

  // Track this component visibility
  useEffectOnce(() => {
    trackContent({ content: 'cta', type: 'popup' });
  });

  return (
    <div
      aria-hidden={!debouncedVisible}
      className={`fixed w-full flex justify-center z-30 bottom-[78px] sm:bottom-6 ${
        styles['popup']
      } ${debouncedVisible && styles['popup--visible']}`}
    >
      <div
        className={`bg-skapa-elevation-2 p-6 rounded-md w-full max-w-[328px] ${styles['box']}`}
      >
        {confirm ? (
          <CtaPopupConfirm onDismiss={() => setVisible(false)} />
        ) : (
          <CtaPopupScore onSelectScore={handleSendScore} />
        )}
      </div>
    </div>
  );
};

// This component renders the CTA form using an animation
const CtaPopupHandler: FC = () => {
  const dispatch = useAppDispatch();
  const { statsPage, createdCode, sessionCounter, shown } = useCtaState();
  // Ref is used to store the initial state
  const ctaShown = useRef(shown);
  // Show CTA if user has visited at least 2 pages and CTA has not been shown
  const shouldRender =
    (statsPage || createdCode || sessionCounter >= 2) && !ctaShown.current;

  // Notify CTA has been shown to avoid showing it again
  useEffect(() => {
    shouldRender && dispatch(setCtaValue({ key: 'shown', value: true }));
  }, [dispatch, shouldRender]);

  // Just render the popup if the conditions are met
  return shouldRender ? <CtaPopup /> : null;
};

const CtaHandler: FC<PropsWithChildren> = ({ children }) => {
  // Counts the number of sessions longer than 2 minutes has a logged in user
  useCtaReportSession();

  return (
    <>
      <CtaPopupHandler />

      {children}
    </>
  );
};

export default CtaHandler;
